import React from "react";
import PropTypes from "prop-types";
import { Button } from "@mui/material";
import { useSelector } from "react-redux";
import messages from "../../../assets/locale/messages";
import { clx } from "../../../utils/Helpers";

import "./_UIButton.scss";

const DESIGN_VARIANTS = [
  "primary",
  "secondary",
  "outline",
  "ghost",
  "destructive",
  "link",
  "success",
];

const COLOR_VARIANT_MAP = {
  error: "destructive",
  destructive: "destructive",
  success: "success",
  warning: "secondary",
  secondary: "secondary",
  primary: "primary",
};

const getButtonTone = ({ appearance, color, variant }) => {
  if (DESIGN_VARIANTS.includes(variant)) return variant;
  if (appearance === "gradient") return "primary";
  if (variant === "text") return "link";
  if (variant === "outlined" && !color) return "outline";
  if (variant === "outlined" && color === "inherit") return "outline";
  return COLOR_VARIANT_MAP[color] || "primary";
};

const getButtonAppearance = ({ appearance, variant, tone }) => {
  if (tone === "outline" || tone === "link") return "base";
  if (tone === "ghost") return appearance === "outline" ? "outline" : "base";
  if (appearance === "outline" || variant === "outlined") return "outline";
  return "solid";
};

const UIButton = ({
  children,
  startIcon = null,
  endIcon = null,
  loading = false,
  loadingLabel,
  disabled = false,
  variant = "contained",
  color,
  size = "medium",
  fullWidth = false,
  appearance = "default",
  className = "",
  ...props
}) => {
  const lang = useSelector((state) => state.locale.lang);
  const shared = messages[lang]?.shared || messages.en.shared;
  const tone = getButtonTone({ appearance, color, variant });
  const buttonAppearance = getButtonAppearance({ appearance, variant, tone });
  const loadingText = loadingLabel || shared.loading;
  const buttonClassName = clx(
    "ui-button",
    `ui-button--${tone}`,
    `ui-button--${buttonAppearance}`,
    `ui-button--${size}`,
    color === "inherit" && variant !== "outline" && "ui-button--inherit",
    loading && "ui-button--loading",
    fullWidth && "ui-button--full-width",
    className
  );

  return (
    <Button
      variant="text"
      color="inherit"
      size="medium"
      fullWidth={fullWidth}
      className={buttonClassName}
      disabled={disabled || loading}
      startIcon={
        loading ? (
          <span className="ui-button__loader" aria-hidden="true" />
        ) : (
          startIcon
        )
      }
      endIcon={loading ? null : endIcon}
      aria-busy={loading ? "true" : undefined}
      {...props}
    >
      <span className="ui-button__content">
        {loading ? loadingText : children}
      </span>
    </Button>
  );
};

UIButton.propTypes = {
  children: PropTypes.node,
  startIcon: PropTypes.node,
  endIcon: PropTypes.node,
  loading: PropTypes.bool,
  loadingLabel: PropTypes.string,
  disabled: PropTypes.bool,
  variant: PropTypes.oneOf([
    "contained",
    "outlined",
    "text",
    "primary",
    "secondary",
    "outline",
    "ghost",
    "destructive",
    "link",
    "success",
  ]),
  color: PropTypes.string,
  size: PropTypes.oneOf(["small", "medium", "large"]),
  fullWidth: PropTypes.bool,
  appearance: PropTypes.oneOf(["default", "gradient", "solid", "outline"]),
  className: PropTypes.string,
};

UIButton.defaultProps = {
  children: null,
  startIcon: null,
  endIcon: null,
  loading: false,
  loadingLabel: undefined,
  disabled: false,
  variant: "contained",
  color: undefined,
  size: "medium",
  fullWidth: false,
  appearance: "default",
  className: "",
};

export default UIButton;
